import {z} from 'zod';
import dedent from 'dedent';
import {defineToolRegistryManifest} from '../../../types/base-tool-types';

const toolName = 'seo-generate-page-settings' as const;

export const pageSettingsInputSchema = z.object({
  pageId: z.string().describe('The ID of the page'),
  slug: z.string().describe('The page slug'),
  seoTitle: z
    .string()
    .optional()
    .describe('Existing SEO title (preserved if provided)'),
  seoDesc: z
    .string()
    .optional()
    .describe('Existing SEO description (preserved if provided)'),
  tokens: z
    .array(z.string())
    .optional()
    .describe(
      'CMS field token paths for dynamic/template pages (e.g. ["name", "slug", "author:name"])'
    ),
});

export type PageSettingsInput = z.infer<typeof pageSettingsInputSchema>;

export const generatePageSettingsArgsSchema = z.object({
  siteId: z.string().describe('The ID of the site'),
  pages: z
    .array(pageSettingsInputSchema)
    .describe('List of pages to generate SEO settings for'),
  localeTag: z
    .string()
    .optional()
    .describe(
      "ISO language code of the site's primary locale (e.g. en, de, fr-FR). When set, generated metadata is written in this language."
    ),
});

export type GeneratePageSettingsArgs = z.infer<
  typeof generatePageSettingsArgsSchema
>;

export const pageSettingsResultItemSchema = z.object({
  pageId: z.string(),
  seoTitle: z.string(),
  seoDesc: z.string(),
  jsonLdSchema: z.record(z.unknown()).nullable(),
  error: z.string().optional(),
});

export type PageSettingsResultItem = z.infer<
  typeof pageSettingsResultItemSchema
>;

export const generatePageSettingsResultSchema = z.object({
  results: z.array(pageSettingsResultItemSchema),
  successCount: z.number().describe('Number of pages successfully generated'),
  failureCount: z.number().describe('Number of pages that failed generation'),
});

export type GeneratePageSettingsResult = z.infer<
  typeof generatePageSettingsResultSchema
>;

export const generatePageSettingsManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Generate page SEO settings',
  prettyStatus: {
    executed: 'Generated page SEO settings',
    executing: 'Generating page SEO settings',
    failed: 'Failed to generate page SEO settings',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Generates SEO metadata (meta title, meta description, and JSON-LD schema)
      for one or more pages using AI. Renders each page's DOM to HTML and uses
      the content to generate appropriate metadata.

      Processes pages concurrently in batches. Each page requires an LLM call,
      so this tool may take time for large numbers of pages.
    `,
    inputSchema: generatePageSettingsArgsSchema,
  },
  type: 'server',
  permission: [
    {
      resource: 'SitePageSettings',
      action: 'manage',
      resolveResources: (args) =>
        args.pages.map((page) => ({
          type: 'SitePageSettings' as const,
          id: page.pageId,
        })),
    },
  ],
  confirmBeforeExecuting: false,
  timeout: 600000,
});
